import { Fragment, type ReactNode } from "react";
import { Link } from "react-router-dom";

/**
 * Minimal Markdown renderer for chatbot replies.
 *
 * The knowledge base and the model both emit `**bold**`, `*italic*`, bullet
 * lists and numbered lists. This builds React elements directly rather than
 * setting innerHTML: the text is model output, so treating it as HTML would be
 * an injection path straight from an untrusted source into the DOM.
 *
 * Anything it does not recognise falls through as plain text — worst case a
 * literal asterisk is shown, never markup that executes.
 */

const INLINE = /(\[[^\]\n]+\]\([^)\s]+\)|\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;

const LINK = /^\[([^\]\n]+)\]\(([^)\s]+)\)$/;

/**
 * Decides whether a link target from the reply may become an `href`.
 *
 * Replies are model output, and the system prompt asks for links, so this is a
 * path from an untrusted source straight to a navigation target: only in-app
 * paths and http(s) URLs are allowed, which leaves `javascript:` and `data:`
 * with nowhere to go. An unusable target still shows its label as plain text —
 * the sentence stays readable, it just is not clickable.
 */
function safeHref(raw: string): { href: string; external: boolean } | null {
  // `//host` is protocol-relative and leaves the app, so it is not "internal".
  if (raw.startsWith("/") && !raw.startsWith("//")) return { href: raw, external: false };
  if (/^https?:\/\//i.test(raw)) return { href: raw, external: true };
  return null;
}

const LINK_CLASS = "text-accent underline underline-offset-2 hover:no-underline";

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  return text.split(INLINE).filter(Boolean).map((part, index) => {
    const key = `${keyPrefix}-${index}`;
    const link = LINK.exec(part);
    if (link) {
      const target = safeHref(link[2]);
      if (!target) return <Fragment key={key}>{link[1]}</Fragment>;
      if (target.external) {
        return (
          <a
            key={key}
            href={target.href}
            target="_blank"
            rel="noopener noreferrer"
            className={LINK_CLASS}
          >
            {link[1]}
          </a>
        );
      }
      return (
        <Link key={key} to={target.href} className={LINK_CLASS}>
          {link[1]}
        </Link>
      );
    }
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      // Recursive, because emphasis wraps links often enough to matter: the
      // model likes to write **[formulir pendaftaran online](/patient/register)**,
      // and treating the inside as plain text showed the raw markdown to the
      // patient instead of a working link. Terminates because the emphasis
      // pattern excludes `*`, so the inner text cannot match again.
      return <strong key={key}>{renderInline(part.slice(2, -2), `${key}-b`)}</strong>;
    }
    if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      return (
        <code key={key} className="bg-surface-sunken rounded px-1 py-0.5 font-mono text-xs">
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith("*") && part.endsWith("*") && part.length > 2) {
      return <em key={key}>{renderInline(part.slice(1, -1), `${key}-i`)}</em>;
    }
    return <Fragment key={key}>{part}</Fragment>;
  });
}

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "h"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "table"; rows: string[][] };

/** A row of `|---|:--:|` under the header, which carries no data itself. */
const SEPARATOR_CELL = /^:?-{1,}:?$/;

const TABLE_ROW = /^\s*\|(.*)\|\s*$/;

function tableCells(line: string): string[] | null {
  const match = TABLE_ROW.exec(line);
  if (!match) return null;
  return match[1].split("|").map((cell) => cell.trim());
}

function isSeparatorRow(cells: string[]): boolean {
  return cells.every((cell) => SEPARATOR_CELL.test(cell));
}

function toBlocks(source: string): Block[] {
  const blocks: Block[] = [];

  for (const raw of source.split("\n")) {
    const line = raw.trimEnd();
    const heading = /^\s*#{1,6}\s+(.*)$/.exec(line);
    const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    const cells = tableCells(line);
    const last = blocks.at(-1);

    if (cells) {
      if (last?.kind === "table") last.rows.push(cells);
      else blocks.push({ kind: "table", rows: [cells] });
    } else if (heading) {
      // Models emit ### headings freely. Rendered at one visual weight rather
      // than h1-h6: this sits inside a chat bubble, and a real heading level
      // here would corrupt the page's document outline for screen readers.
      blocks.push({ kind: "h", text: heading[1] });
    } else if (bullet) {
      if (last?.kind === "ul") last.items.push(bullet[1]);
      else blocks.push({ kind: "ul", items: [bullet[1]] });
    } else if (numbered) {
      if (last?.kind === "ol") last.items.push(numbered[1]);
      else blocks.push({ kind: "ol", items: [numbered[1]] });
    } else if (line.trim() === "") {
      // Blank line closes the current block.
      if (last?.kind === "p") blocks.push({ kind: "p", lines: [] });
    } else if (last?.kind === "p" && last.lines.length > 0) {
      last.lines.push(line);
    } else {
      blocks.push({ kind: "p", lines: [line] });
    }
  }

  return blocks.filter((block) => {
    if (block.kind === "p") return block.lines.length > 0;
    if (block.kind === "h") return block.text.trim().length > 0;
    if (block.kind === "table") {
      // A table that is nothing but its own header rule carries no content.
      block.rows = block.rows.filter((row) => !isSeparatorRow(row));
      return block.rows.length > 0;
    }
    return block.items.length > 0;
  });
}

export function RichText({ text, className }: { text: string; className?: string }) {
  const blocks = toBlocks(text);

  return (
    <div className={className}>
      {blocks.map((block, index) => {
        if (block.kind === "h") {
          return (
            <p key={index} className="mt-3 mb-1 font-bold first:mt-0">
              {renderInline(block.text, `${index}-h`)}
            </p>
          );
        }
        if (block.kind === "ul") {
          return (
            <ul key={index} className="my-1.5 list-disc space-y-0.5 pl-5">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item, `${index}-${itemIndex}`)}</li>
              ))}
            </ul>
          );
        }
        if (block.kind === "table") {
          const [header, ...body] = block.rows;
          const columns = Math.max(...block.rows.map((row) => row.length));
          return (
            // The bubble is narrower than the page, so a wide table scrolls
            // inside its own box rather than stretching the conversation.
            <div
              key={index}
              className="border-hairline my-2 overflow-x-auto rounded-lg border first:mt-0 last:mb-0"
            >
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="bg-surface-sunken">
                    {Array.from({ length: columns }, (_, cell) => (
                      <th
                        key={cell}
                        scope="col"
                        className="border-hairline border-b px-3 py-2 font-semibold whitespace-nowrap"
                      >
                        {renderInline(header[cell] ?? "", `${index}-th-${cell}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {body.map((row, rowIndex) => (
                    <tr key={rowIndex} className="border-hairline border-b last:border-b-0">
                      {Array.from({ length: columns }, (_, cell) => (
                        <td key={cell} className="px-3 py-2 align-top">
                          {renderInline(row[cell] ?? "", `${index}-${rowIndex}-${cell}`)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.kind === "ol") {
          return (
            <ol key={index} className="my-1.5 list-decimal space-y-0.5 pl-5">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item, `${index}-${itemIndex}`)}</li>
              ))}
            </ol>
          );
        }
        return (
          <p key={index} className="my-1.5 first:mt-0 last:mb-0">
            {block.lines.map((line, lineIndex) => (
              <Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {renderInline(line, `${index}-${lineIndex}`)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
